import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';

import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import { ITag } from '../../ts/interface/problem.interface';
import { ticketColumns } from '../../tableColumns/maintenanceColumns';
import {
  IColumn,
  ISortConfig,
} from '../../ts/interface/components/redesing/table.interface';
import { SearchChip } from '../../ts/interface/components/table.interface';

import useUrlState from '../../hooks/use-url-state';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';
import {
  useMaintenanceTicketsRelayQuery,
  parseStatusFilterParam,
} from '../../hooks/maintenance/query/V2/useMaintenanceTicketsRelay';
import { useMaintenanceTicketTagsRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceTicketTagsRelay';
import {
  mapMaintenanceTicketsToLegacy,
  mapMaintenanceTagsToLegacy,
} from '../../hooks/maintenance/query/V2/maintenanceMappers';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MovaticFieldGroupSelectFilter } from '../../components/Redesing/movatic-field-group';
import AddIcon from '@mui/icons-material/Add';
import { history } from '../../utils';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import useExportMaintenanceTickets from '../../hooks/maintenance/query/useExportMaintenanceTickets';

interface IMaintenanceTicketProps {
  systemLoaded: boolean;
  system: ISystem;
  mode: IMode;
}

interface IFilters {
  label: string;
  value: string;
}

const filterOptions = [
  { value: 'open', label: 'Open', disabled: true },
  { value: 'closed', label: 'Closed' },
  { value: 'canceled', label: 'Canceled' },
];

// Map legacy sort keys to GraphQL sort field names
const sortKeyMap: Record<string, string> = {
  created_on: 'CREATED_ON',
  due_on: 'DUE_ON',
  priority: 'PRIORITY',
  status: 'STATUS',
  assignee: 'ASSIGNEE',
  hardware_number: 'HARDWARE_OLD_ID',
  completed_on: 'COMPLETED_ON',
  canceled_on: 'CANCELED_ON',
  description: 'DESCRIPTION',
};

const MaintenanceTicket: React.FC<IMaintenanceTicketProps> = (props) => {
  const { mode, system } = props;
  const { unitsTitle } = mode.details;
  const { new_hardware } = system;

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    status: 'open',
    tags: '',
    // Pending (dropdown) selections applied via onApplyFilter.
    statusFilter: [{ label: 'Open', value: 'open' }] as IFilters[],
    tagFilter: [] as IFilters[],
    sortConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
  });

  const debouncePatternMaintenance = useSearchDebounce(filters.search);

  const { data: tagsDataRaw = [], isLoading: isTagsLoading } =
    useMaintenanceTicketTagsRelayQuery(
      { systemId: system.gid },
      { enabled: props.systemLoaded }
    );

  const tagsData = useMemo(
    () => (tagsDataRaw ? mapMaintenanceTagsToLegacy(tagsDataRaw) : []),
    [tagsDataRaw]
  );

  const loadedTags = useMemo(
    () =>
      tagsData.map((item: ITag) => {
        return {
          label: item.name,
          value: item.name,
        };
      }),
    [tagsData]
  );

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Rebuild the chip list from the *applied*, URL-persisted filters that drive
  // the query, so the chips always match the fetched data — after a refresh and
  // when a filter is cleared (no chip). Skip while URL state is still restoring.
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];

    filters.status
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        const option = filterOptions.find((o) => o.value === value);
        next.push({
          label: 'Status',
          field: 'status',
          value,
          displayValue: option?.label ?? value,
          disabled: false,
        });
      });

    filters.tags
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        next.push({ label: 'Tags', field: 'tags', value, displayValue: value });
      });

    setChips(next);
  }, [isRestoring, filters.status, filters.tags]);

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const field = sortKeyMap[filters.sortConfig?.key || 'created_on'] || 'CREATED_ON';
    const order =
      filters.sortConfig?.direction?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
    return [{ field: field as any, order: order as 'ASC' | 'DESC' }];
  }, [filters.sortConfig]);

  // Parse status filter - supports comma-separated multi-select values.
  // No selection means no status filtering at all.
  const statusFilter = useMemo(
    () => parseStatusFilterParam(filters.status),
    [filters.status]
  );

  // Parse tag filter
  const tagFilter = useMemo(() => {
    return filters.tags ? filters.tags.split(',').filter(Boolean) : undefined;
  }, [filters.tags]);

  const {
    data: ticketsDataRaw,
    count: totalTickets,
    isLoading: loadingTickets,
    isRefetching: fetchingTickets,
    hasMore,
    endCursor,
  } = useMaintenanceTicketsRelayQuery(
    {
      systemId: system.gid,
      status: statusFilter,
      tags: tagFilter,
      search: debouncePatternMaintenance || undefined,
    },
    {
      limit: filters.sizePerPage,
      sort: sortConfig,
      enabled: isComplete && props.systemLoaded,
      after,
    }
  );

  // Only the current page's rows are shown (server-side pagination).
  const ticketItems = useMemo(
    () => (ticketsDataRaw ? mapMaintenanceTicketsToLegacy(ticketsDataRaw) : []),
    [ticketsDataRaw]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsDataRaw]);

  const columnsDis = useMemo(() => {
    return ticketColumns(
      unitsTitle,
      new_hardware,
      statusFilter.length > 0 ? statusFilter : ['all']
    );
  }, [unitsTitle, new_hardware, statusFilter]);

  const handleChipsChange = useCallback(
    (
      values: string[],
      list: { label: string; value: string }[],
      metadata: { label: string; value: string }
    ): void => {
      resetPagination();
      setFilters((prevState) => ({
        ...prevState,
        [metadata.value]: values.join(','),
      }));

      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== metadata.value) {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }

        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = list.find((option) => option.value.toString() === value);
            newChips.push({
              label: metadata.label,
              field: metadata.value,
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    [setFilters, resetPagination]
  );

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });

      const chipToDelete = deletedGroup.label.toLowerCase() as 'status' | 'tags';

      resetPagination();
      setFilters((prevState) => ({
        ...prevState,
        [chipToDelete]: '',
      }));

      const deleteChip = {
        status: () =>
          setFilters((prevState) => ({ ...prevState, statusFilter: [] })),
        tags: () => setFilters((prevState) => ({ ...prevState, tagFilter: [] })),
      };

      if (deleteChip[chipToDelete]) {
        deleteChip[chipToDelete]();
      }
    },
    [setFilters, resetPagination]
  );

  const { mutateAsync: exportMaintenanceTickets } = useExportMaintenanceTickets();

  const handleExportData = useCallback(() => {
    CustomAsyncToast({
      promise: () =>
        exportMaintenanceTickets({
          status: filters.status,
          tags: filters.tags,
          search: debouncePatternMaintenance || undefined,
        }),
      successMessage: () => 'Maintenance tickets exported successfully',
      loadingMessage: 'Exporting maintenance tickets...',
      errorMessage: 'Error exporting maintenance tickets',
    });
  }, [
    exportMaintenanceTickets,
    filters.status,
    filters.tags,
    debouncePatternMaintenance,
  ]);

  return (
    <PaginationTable
      totalItems={totalTickets || 0}
      isLoading={loadingTickets || !isComplete || isPaginating}
      isLoadingCache={fetchingTickets}
      hasMore={hasMore}
      noDataSlice
      localSearch={false}
      showFilter
      padding={{ pt: 1, pl: 0, pr: 1 }}
      chips={chips}
      handleChipDelete={handleChipDelete}
      buttons={[
        {
          label: 'Export',
          startIcon: <FileDownloadIcon />,
          onClick: handleExportData,
        },
        {
          label: 'Add',
          onClick: () => history.push('/maintenance/add'),
          startIcon: <AddIcon />,
        },
      ]}
      onApplyFilter={() => {
        if (
          filters.status !==
          filters.statusFilter
            .filter((option) => filterOptions.some((item) => item.label === option.label))
            .map((item) => item.value.toString())
            .join(',')
        ) {
          handleChipsChange(
            [
              ...filters.statusFilter
                .filter((option) =>
                  filterOptions.some((item) => item.label === option.label)
                )
                .map((item) => item.value.toString()),
            ],
            filterOptions,
            { label: 'Status', value: 'status' }
          );
        }

        if (
          filters.tags !==
          filters.tagFilter.map((item) => item.value.toString()).join(',')
        ) {
          handleChipsChange(
            filters.tagFilter.map((item) => item.value.toString()),
            loadedTags,
            { label: 'Tags', value: 'tags' }
          );
        }
      }}
      spaceFilters={
        <>
          <MovaticFieldGroupSelectFilter
            label={'Status'}
            value={filters.statusFilter}
            onChange={(event: React.SyntheticEvent, newValue: any[]) => {
              setFilters((prevState) => ({
                ...prevState,
                statusFilter: [
                  ...newValue.filter((option) =>
                    filterOptions.some((item) => item.label === option.label)
                  ),
                ],
              }));
            }}
            options={filterOptions}
          />
          <MovaticFieldGroupSelectFilter
            label={'Tags'}
            isLoading={isTagsLoading}
            value={filters.tagFilter}
            onChange={(event, newValue) => {
              setFilters((prevState) => ({
                ...prevState,
                tagFilter: [
                  ...newValue.filter((option) =>
                    loadedTags.some(
                      (item: { label: string }) => item.label === option.label
                    )
                  ),
                ],
              }));
            }}
            options={loadedTags}
          />
        </>
      }
      customKey="gid"
      onRowCLick={(id: string) => history.push(`/maintenance/${id}`)}
      sortBy={filters.sortConfig.key}
      onSortChange={(sort) => {
        resetPagination();
        setFilters((prevState) => ({
          ...prevState,
          sortConfig: sort as ISortConfig,
        }));
      }}
      columns={columnsDis as IColumn[]}
      items={ticketItems}
      searchPlaceholder={`Search tickets`}
      page={pageNumber}
      rowsPerPage={filters.sizePerPage}
      onPageChange={(page, newPage) => onPageChange(newPage)}
      onRowsPerPageChange={(pageSizeString) => {
        const pageSize = Number(pageSizeString.target.value);
        resetPagination();
        setFilters((prevState) => ({
          ...prevState,
          sizePerPage: pageSize,
        }));
      }}
      handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
        resetPagination();
        setFilters((prevState) => ({
          ...prevState,
          search: e.target.value,
        }));
      }}
      searchText={filters.search}
    />
  );
};

export default connect(
  (state: { system: { isLoaded: boolean; current: ISystem }; mode: IMode }) => ({
    systemLoaded: state.system.isLoaded,
    system: state.system.current,
    mode: state.mode,
  }),
  () => ({})
)(MaintenanceTicket);
